import "../../../Components/Charts/AreaChart/AreaChart.scss";
import React, { Fragment, useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Areas } from "../../../Components/Charts/AreaChart/AreaChart";
import { LineDataKeys } from "../../../Components/Charts/LineChart/LineChart";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  Area,
  AreaChart as AC,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  Line,
  ComposedChart,
} from "recharts";
import ChartTooltip, { PayloadItem } from "../../../Components/Charts/ChartTooltip/ChartTooltip";
import { colorMap as colors } from "../../../utils/colors";
import * as R from "ramda";
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE,
} from "../../../Components/Charts/ChartConstants";
import { headerFormatter, useDynamicYAxisWidth } from "../../../Components/Charts/ChartUtils";
import { useCompanyInfo } from "../../../redux/company";
import DefaultLegend from "../../../Components/Charts/Legend/DefaultLegend/DefaultLegend";
import LegendWithGrouping from "../../../Components/Charts/Legend/LegendWithGrouping";
import { capitalizeWords } from "../../../MMM/MMMUtils";
import { ConfidenceLegend } from "./ConfidenceLegend";
const GRAY_COLOR = "#94a0b8";

interface OutcomeAndResponseChartProps {
  areas: Areas | LineDataKeys;
  activeArea: string | null;
  setActiveArea: React.Dispatch<React.SetStateAction<string | null>>;
  activeGroup: string | null;
  setActiveGroup: React.Dispatch<React.SetStateAction<string | null>>;
  data: any[];
  dateGrouping?: string;
  colorOverrides?: { [key: string]: string };
  hardCodedColor: string;
  hardCodedColorName: string;
  hideLegend?: boolean;
  groupedAreas?: any[];
  incrementality?: boolean;
  syncId?: number;
  tooltipFormatter?: (value: any) => string;
  tooltipShape?: "circle" | "square" | "line";
  reverseToolTipItems?: boolean;
  reverseLegend?: boolean;
  usePercentageYAxis?: boolean;
  xAxisDataKey: string;
  xAxisTickFormatter?: (value: any) => string;
  yAxisTickFormatter?: (value: any) => string;
  yAxisWidth?: number;
  legendTitle?: string;
  groupBy: string;
  isResponse?: boolean;
  logScale?: boolean;
  setLogScale?: (value: boolean) => void;
}

export const OutcomeAndResponseChart: React.FC<OutcomeAndResponseChartProps> = ({
  areas,
  activeArea,
  setActiveArea,
  activeGroup,
  setActiveGroup,
  data,
  dateGrouping,
  colorOverrides,
  hardCodedColor,
  hardCodedColorName,
  groupedAreas,
  usePercentageYAxis = false,
  xAxisDataKey,
  xAxisTickFormatter,
  yAxisTickFormatter,
  legendTitle,
  tooltipShape,
  reverseToolTipItems,
  reverseLegend,
  tooltipFormatter,
  groupBy,
  isResponse = false,
  logScale = false,
  setLogScale = () => {},
}) => {
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    data,
  });

  const activeAreaRef = useRef<string | null>(null);
  const activeGroupRef = useRef<string | null>(null);
  useEffect(() => {
    // Don't reset activeArea/activeGroup if they were set by a legend click
    if (activeAreaRef.current === null) {
      setActiveArea(null);
      setActiveGroup(null);
      setHoveredLegend(null);
    }
  }, [groupBy, isResponse, data, areas, groupedAreas, setActiveArea, setActiveGroup]);

  // Transform data for log scale if needed
  const transformedData = useMemo(() => {
    if (!logScale || !data || R.isEmpty(data)) {
      return data;
    }

    return data.map(item => {
      const transformedItem = { ...item };
      Object.keys(transformedItem).forEach(key => {
        if (key !== xAxisDataKey && typeof transformedItem[key] === "number") {
          const value = transformedItem[key] || -1;
          // Ignore any values less than 1 for log scale
          transformedItem[key] = value < 1 ? null : value;
        }
      });
      return transformedItem;
    });
  }, [data, logScale, xAxisDataKey]);

  const colorMap = useMemo(() => {
    if (!R.isEmpty(areas)) {
      const colorsOriginal = colors(areas, hardCodedColorName, hardCodedColor);

      return { ...colorsOriginal, ...colorOverrides };
    }
  }, [areas, hardCodedColorName, hardCodedColor, colorOverrides]);

  const handleLegendClick = useCallback(
    (line: string, groupChannel?: string) => {
      if (activeArea === line) {
        setActiveArea(null);
        setActiveGroup(null);
        activeAreaRef.current = null;
        activeGroupRef.current = null;
      } else {
        setLogScale(false);
        setActiveArea(line);
        activeAreaRef.current = line;
        if (groupChannel) {
          setActiveGroup(groupChannel);
          activeGroupRef.current = groupChannel;
        }
      }
    },
    [activeArea, setActiveArea, setActiveGroup, setLogScale]
  );

  const handleLegendEnter = useCallback(
    line => {
      hoveredLegend === line ? setHoveredLegend(null) : setHoveredLegend(line);
    },
    [hoveredLegend]
  );

  const buildItems = useCallback(
    (chartData, activeLine, hoveredLine, isArea) => {
      const lines = chartData.map((data, index: number) => {
        const isActiveLine = activeLine === null ? true : activeLine === data.name;
        const isConfidenceView = activeLine !== null;
        const isHoveredLegend = hoveredLine === null ? true : hoveredLine === data.name;
        const activeColor = isActiveLine ? colorMap[data.name] : GRAY_COLOR;
        const hoverColor = isHoveredLegend ? colorMap[data.name] : GRAY_COLOR;
        if (!isActiveLine && activeLine !== null) {
          return null;
        }

        return isArea ? (
          <Area
            activeDot={false}
            dataKey={data.dataKey}
            fill={activeArea ? activeColor : hoverColor}
            fillOpacity={1}
            isAnimationActive={false}
            key={`area-${index}`}
            name={data.name}
            stackId="1"
            stroke={activeArea ? activeColor : hoverColor}
            strokeWidth={0}
            type="monotone"
          />
        ) : (
          <Fragment key={`line-${index}`}>
            {isConfidenceView && (
              <Fragment key={`confidence-${index}`}>
                <Area
                  type="linear"
                  dataKey={`${data.dataKey}_$lower`}
                  stroke="#EED2FF"
                  fillOpacity={0}
                  strokeWidth={0}
                  dot={false}
                  activeDot={false}
                  stackId="confidence"
                  key={`lower-${index}`}
                />
                <Area
                  type="linear"
                  dataKey={`${data.dataKey}_$band`}
                  fill={activeArea ? activeColor : hoverColor}
                  fillOpacity={0.25}
                  strokeWidth={0}
                  activeDot={false}
                  dot={false}
                  stackId="confidence"
                  key={`band-${index}`}
                />
              </Fragment>
            )}
            <Line
              dataKey={data.dataKey}
              dot={false}
              isAnimationActive={false}
              key={`line-element-${index}`}
              stroke={activeLine ? activeColor : hoverColor}
              strokeWidth={3}
              strokeDasharray={
                isConfidenceView && !String(data.dataKey).includes("baseline") ? "4" : undefined
              }
              type="monotone"
            />
          </Fragment>
        );
      });
      return lines;
    },
    [colorMap, activeArea]
  );

  const chart = useMemo(() => {
    const percentageYAxis = (
      <YAxis
        domain={[0, 100]}
        orientation="left"
        tick={TICK_STYLE}
        ticks={[0, 25, 50, 75, 100]}
        type="number"
        yAxisId="0"
        unit="%"
      />
    );
    const leftYAxis = (
      <YAxis
        axisLine={false}
        domain={["auto", "auto"]}
        orientation="left"
        tick={TICK_STYLE}
        tickFormatter={tick => `${yAxisTickFormatter ? yAxisTickFormatter(tick) : tick}`}
        tickLine={false}
        type="number"
        width={yAxisWidth}
        scale={logScale ? "log" : "linear"}
      />
    );
    const xxAxis = (
      <XAxis
        axisLine={false}
        dataKey={xAxisDataKey}
        height={xAxisHeight}
        tick={TICK_STYLE}
        tickFormatter={xAxisTickFormatter}
      />
    );
    const useLeftAxis = true && !usePercentageYAxis;
    const lineTooltip = (
      <Tooltip
        content={({ active, label, payload }) => {
          if (active && payload && payload[0]) {
            const standardFormatter = val => (val ? `${val.toFixed(2)}` : val);
            if (activeArea === null) {
              const formattedHeaderLabel = headerFormatter(dateGrouping, label, fiscalCal);
              let items = payload.map(item => ({
                label: capitalizeWords(item.name as string),
                value: tooltipFormatter
                  ? tooltipFormatter(item.value)
                  : standardFormatter(item.value),
                color: item.stroke,
              }));
              items = R.sortBy((item: any) => -Math.abs(item.value), items);

              return (
                <ChartTooltip headerLabel={formattedHeaderLabel} items={items} shape={"line"} />
              );
            } else if (!R.isNil(payload[2])) {
              const item = payload[2]; // hard coded because I know theres 3 chart items in this case... not ideal
              if (R.isNil(item.value) || isNaN(Number(item.value))) {
                return null;
              }
              let lowerValue, upperValue;
              const isBaseline = String(item.name).includes("baseline");

              if (!R.isNil(item.payload[`${item.name}_$lower`]) && !isBaseline) {
                const lowVal = item.payload[`${item.name}_$lower`];
                lowerValue = tooltipFormatter ? tooltipFormatter(lowVal) : (lowVal as number);
              }
              if (!R.isNil(item.payload[`${item.name}_$upper`]) && !isBaseline) {
                const upVal = item.payload[`${item.name}_$upper`];
                upperValue = tooltipFormatter ? tooltipFormatter(upVal) : (upVal as number);
              }

              const formattedHeaderLabel = `${capitalizeWords(String(item.name))} ${headerFormatter(
                dateGrouping,
                label,
                fiscalCal
              )}`;
              const svgIcon = isBaseline ? undefined : (
                <ConfidenceLegend
                  showBackground={false}
                  color={colorMap[activeArea]}
                  backgroundColor={colorMap[activeArea]}
                />
              );
              let items: PayloadItem[] = [
                {
                  label: isBaseline ? "Baseline" : "Estimate",
                  value: tooltipFormatter
                    ? tooltipFormatter(item.value)
                    : ((item.value as unknown) as number),
                  color: colorMap[activeArea],
                  shape: "line",
                  svgIcon,
                },
              ];
              if (!R.isNil(lowerValue) && !R.isNil(upperValue)) {
                items.push({
                  label: "Confidence",
                  value: `${lowerValue} - ${upperValue}`,
                  color: colorMap[activeArea],
                  shape: "square",
                  iconClassName: "partialOpcatiy",
                });
              }
              return (
                <ChartTooltip
                  headerLabel={formattedHeaderLabel}
                  items={items}
                  shape={tooltipShape}
                />
              );
            }
          }
          return null;
        }}
        isAnimationActive={false}
      />
    );
    return activeArea === null && !(isResponse || logScale) ? (
      <AutoSizer key={"outcomeAndResponseGeoMMMArea"}>
        {({ width, height }) => (
          <AC data={transformedData} height={height} ref={setChartRef} width={width}>
            <CartesianGrid
              stroke={CARTESIAN_GRID_STROKE}
              strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
              vertical={false}
            />
            {xxAxis}
            {usePercentageYAxis && percentageYAxis}
            {!usePercentageYAxis && (
              <YAxis
                axisLine={false}
                tick={TICK_STYLE}
                tickFormatter={yAxisTickFormatter}
                tickLine={false}
                width={yAxisWidth}
              />
            )}
            {buildItems(areas, activeArea, hoveredLegend, true)}
            <Tooltip
              content={({ label, payload }) => {
                if (!payload || !label || !dateGrouping) {
                  return null;
                }
                const header = headerFormatter(dateGrouping, label, fiscalCal);
                let items = R.sortBy(
                  (item: any) => -Math.abs(item.value),
                  payload?.map(item => {
                    return {
                      label: item.name as string,
                      value: tooltipFormatter
                        ? tooltipFormatter(item.value)
                        : (item.value as number),
                      color: item.stroke,
                    };
                  }) || []
                );

                return (
                  <ChartTooltip
                    headerLabel={header}
                    items={items}
                    shape={tooltipShape}
                    reverseItems={reverseToolTipItems}
                  />
                );
              }}
              isAnimationActive={false}
            />
          </AC>
        )}
      </AutoSizer>
    ) : (
      <AutoSizer key={"outcomeAndResponseGeoMMMLine"}>
        {({ width, height }) => (
          <ComposedChart data={transformedData} height={height} width={width} ref={setChartRef}>
            <CartesianGrid
              stroke={CARTESIAN_GRID_STROKE}
              strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
              vertical={false}
            />
            {xxAxis}
            {usePercentageYAxis && percentageYAxis}
            {useLeftAxis && leftYAxis}
            {lineTooltip}
            {buildItems(areas, activeArea, hoveredLegend, false)}
          </ComposedChart>
        )}
      </AutoSizer>
    );
  }, [
    yAxisWidth,
    logScale,
    xAxisDataKey,
    xAxisHeight,
    xAxisTickFormatter,
    usePercentageYAxis,
    activeArea,
    isResponse,
    yAxisTickFormatter,
    dateGrouping,
    fiscalCal,
    tooltipFormatter,
    colorMap,
    tooltipShape,
    transformedData,
    setChartRef,
    buildItems,
    areas,
    hoveredLegend,
    reverseToolTipItems,
  ]);

  return (
    <div className="areaChart" style={{ display: "flex", flex: 1 }}>
      <div style={{ display: "flex", flex: 1 }}>{chart}</div>
      <div className={"rightOfChart"}>
        <div className="legendTitle">{legendTitle}</div>
        {groupedAreas && !R.isEmpty(groupedAreas) ? (
          <LegendWithGrouping
            activeArea={activeArea}
            activeGroup={activeGroup}
            handleLegendClick={handleLegendClick}
            setHoveredLegend={setHoveredLegend}
            handleLegendEnter={handleLegendEnter}
            hoveredLegend={hoveredLegend}
            incrementality={false}
            colorMap={colorMap}
            groupedAreas={groupedAreas}
            showDash={logScale || isResponse || activeArea === "Baseline"}
            svg={
              activeArea === "Baseline" ? undefined : (
                <ConfidenceLegend
                  showBackground={true}
                  color={activeArea !== null ? colorMap[activeArea] : "#bd32ff"}
                  backgroundColor={activeArea !== null ? colorMap[activeArea] : "#bd32ff"}
                />
              )
            }
          />
        ) : (
          <DefaultLegend
            areas={areas as Areas}
            activeArea={activeArea}
            activeGroup={activeGroup}
            handleLegendClick={handleLegendClick}
            incrementality={false}
            setHoveredLegend={setHoveredLegend}
            handleLegendEnter={handleLegendEnter}
            hoveredLegend={hoveredLegend}
            colorMap={colorMap}
            reverseLegend={reverseLegend}
            showDash={logScale || isResponse || activeArea === "Baseline"}
            svg={
              activeArea === "Baseline" ? undefined : (
                <ConfidenceLegend
                  showBackground={true}
                  color={activeArea !== null ? colorMap[activeArea] : "#bd32ff"}
                  backgroundColor={activeArea !== null ? colorMap[activeArea] : "#bd32ff"}
                />
              )
            }
          />
        )}
      </div>
    </div>
  );
};
